
import { Component, ComponentProps } from "react";

import { dig } from "@matchlighter/common_library/lib/macro";
import { styleSheet } from "@matchlighter/common_library/lib/StyleSheet";

import { Flex } from "@instructure/ui-flex";
import { Text } from '@instructure/ui-text';
import { View } from "@instructure/ui-view";
import { IconCertifiedLine, IconSearchLine } from '@instructure/ui-icons';

import { API } from "@/api";
import { District } from "@/models/common";
import { AsyncSelect2, FetchUserError, OptionMeta, AsyncMultiSelect, WAsyncSelect2Props } from "./AsyncSelect";
import _styleSheetSrc from '../../routes/purchase_orders/index.scss';

const styles = styleSheet(_styleSheetSrc);type DistrictSelectorProps = WAsyncSelect2Props<District> & {
    includeAnyOption?: boolean | string;
    withArchived?: boolean
}

export class DistrictSelector extends Component<DistrictSelectorProps> {
    fetchDistricts = async (term) => {
        if (term && term.length < 3) throw new FetchUserError("Type at least 3 characters to search");

        const params: any = {};
        if (term) params['search_term'] = term;
        if (this.props.withArchived) params['state'] = 'any';

        const response = await API.get(`api/v1/districts/`, { params })

        const items: any[] = response.data['items'];
        if (this.props.includeAnyOption === true) {
            items.unshift({ id: '0', name: 'Any District/Charter' });
        } else if (this.props.includeAnyOption) {
            items.push({ id: '0', name: this.props.includeAnyOption });
        }
        return items;
    }

    renderOption(dist: any, meta: OptionMeta) {
        return <>
            {dist.name} {dist.po_validation_enabled &&
                <span className={styles("certified-span")}>
                    <IconCertifiedLine />
                </span>
            }
        </>
    }

    render() {
        const { includeAnyOption, ...pass } = this.props
        return <AsyncSelect2
            // minSearchLength={0}
            options={this.fetchDistricts}
            renderOption={this.renderOption}
            {...pass}
        />
    }
}

export class DistrictSearchSelector extends DistrictSelector {

    renderDistrictSearchOption = (dist: any, meta: OptionMeta) => {
        const inverse = (meta.selected || meta.highlighted) ? '-inverse' : '';
        return <>
            <Flex>
                <Flex.Item margin="0 small 0 0">
                    <Text color={'primary' + inverse} >
                        {dist.name}
                    </Text>
                </Flex.Item>
            </Flex>
            <Flex as="div">
                <Flex.Item>
                    {dist.workflow_state == 'archived' ?
                        <View>
                            <Text color="secondary" transform="uppercase" letterSpacing="expanded" size="x-small" weight="bold">
                                {dist.workflow_state}
                            </Text> &nbsp;|&nbsp;&nbsp;</View> : ""
                    }
                </Flex.Item>
                <Flex.Item>
                    <Text color={'primary' + inverse} weight="bold" size="x-small">
                        Code:&nbsp;
                    </Text>
                    <Text color={'primary' + inverse} size="x-small">
                        {dig(dist.district_code)}
                    </Text>
                </Flex.Item>
                <Flex.Item>
                    {dig(dist.educational_service_center.name) ? <View>&nbsp;|&nbsp;&nbsp;
                        <Text color={'primary' + inverse} weight="bold" size="x-small">
                            Region:&nbsp;
                        </Text>
                        <Text color={'primary' + inverse} size="x-small">
                            {dig(dist.educational_service_center.name)}
                        </Text> </View> : ""
                    }
                </Flex.Item>
            </Flex>
        </>
    }

    render() {
        const { ...pass } = this.props
        return <DistrictSelector
            renderOption={this.renderDistrictSearchOption}
            minSearchLength={3}
            icon={IconSearchLine}
            {...pass}
        />
    }
}



type DistrictMultiSelectorProps = Omit<ComponentProps<typeof AsyncMultiSelect>, 'options'> & {
    forAPId?: string
}

export class DistrictSearchMultiSelector extends Component<DistrictMultiSelectorProps> {

    searchDistrict = async (term) => {
        if (term && term.length < 3) throw new FetchUserError("Type at least 3 characters to search");

        const params: any = {};
        if (term) params['search_term'] = term;
        if (this.props.forAPId) params['ap_id'] = this.props.forAPId;

        params['state'] = 'any';

        const response = await API.get(`api/v1/districts/`, { params })
        const items: any[] = response.data['items'];
        return items;
    }

    renderDistrictSearchOption = (dist: any, meta: OptionMeta) => {
        const inverse = (meta.selected || meta.highlighted) ? '-inverse' : '';
        return <>
            <Flex>
                <Flex.Item margin="0 small 0 0">
                    <Text color={'primary' + inverse} >
                        {dist.name}
                    </Text>
                </Flex.Item>
            </Flex>
            <Flex as="div">
                <Flex.Item>
                    {dist.workflow_state == 'archived' ?
                        <View>
                            <Text color="secondary" transform="uppercase" letterSpacing="expanded" size="x-small" weight="bold">
                                {dist.workflow_state}
                            </Text> &nbsp;|&nbsp;&nbsp;</View> : ""
                    }
                </Flex.Item>
                <Flex.Item>
                    <Text color={'primary' + inverse} weight="bold" size="x-small">
                        Code:&nbsp;
                    </Text>
                    <Text color={'secondary' + inverse} size="x-small">
                        {dig(dist.district_code)}
                    </Text>
                </Flex.Item>
                <Flex.Item>
                    {dig(dist.educational_service_center.name) ? <View>&nbsp;|&nbsp;&nbsp;
                        <Text color={'primary' + inverse} weight="bold" size="x-small">
                            Region:&nbsp;
                        </Text>
                        <Text color={'primary' + inverse} size="x-small">
                            {dig(dist.educational_service_center.name)}
                        </Text> </View> : ""
                    }
                </Flex.Item>
            </Flex>
        </>
    }

    render() {
        const { forAPId, ...pass } = this.props
        return <AsyncMultiSelect
            options={this.searchDistrict}
            renderOption={this.renderDistrictSearchOption}
            minSearchLength={3}
            icon={IconSearchLine}
            width='21rem'
            {...pass}
        />
    }
}